import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { LogOut, Plus, Moon, Sun, Database, Folder, FolderOpen, MoreVertical, Edit2, Trash2, FolderPlus, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [projectsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/api/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/folders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(projectsRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Nama project tidak boleh kosong');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/projects`,
        { name: newProjectName, folder_id: selectedFolderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProjects([...projects, response.data]);
      setNewProjectName('');
      setShowCreateDialog(false);
      setSelectedFolderId(null);
      toast.success('Project berhasil dibuat!');
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Nama folder tidak boleh kosong');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/folders`,
        { name: newFolderName, parent_folder_id: selectedFolderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFolders([...folders, response.data]);
      setNewFolderName('');
      setShowCreateFolderDialog(false);
      setSelectedFolderId(null);
      toast.success('Folder berhasil dibuat!');
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm('Yakin ingin menghapus project ini?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success('Project berhasil dihapus');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleDeleteFolder = async (folderId, e) => {
    e.stopPropagation();
    if (!window.confirm('Yakin ingin menghapus folder ini?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(folders.filter(f => f.id !== folderId));
      toast.success('Folder berhasil dihapus');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete folder';
      toast.error(errorMsg);
    }
  };

  const handleDragStart = (project) => {
    setDraggedProject(project);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedProject) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/projects/${draggedProject.id}/move`,
        { folder_id: targetFolderId },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { folder_id: targetFolderId }
        }
      );
      
      // Update local state
      setProjects(projects.map(p => 
        p.id === draggedProject.id ? { ...p, folder_id: targetFolderId } : p
      ));
      
      toast.success(`Project dipindahkan ke ${targetFolderId ? 'folder' : 'root'}`);
    } catch (error) {
      toast.error('Failed to move project');
    } finally {
      setDraggedProject(null);
      setDragOverFolder(null);
    }
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const buildFolderTree = (parentId = null) => {
    return folders
      .filter(f => f.parent_folder_id === parentId)
      .map(folder => ({
        ...folder,
        children: buildFolderTree(folder.id),
        projects: projects.filter(p => p.folder_id === folder.id)
      }));
  };

  const renderFolder = (folder, depth = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children.length > 0 || folder.projects.length > 0;
    const isDragOver = dragOverFolder === folder.id;

    return (
      <div key={folder.id} style={{ marginLeft: `${depth * 24}px` }}>
        <div
          className={`flex items-center space-x-2 p-3 rounded-lg hover:bg-accent/50 cursor-pointer transition-colors ${
            isDragOver ? 'bg-primary/10 border-2 border-primary border-dashed' : 'border border-transparent'
          }`}
          onClick={() => toggleFolder(folder.id)}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, folder.id)}
        >
          <div className="flex items-center flex-1 space-x-2">
            {hasChildren && (
              isExpanded ? 
                <ChevronDown className="w-4 h-4 text-muted-foreground" /> : 
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            {!hasChildren && <div className="w-4" />}
            
            {isExpanded ? 
              <FolderOpen className="w-5 h-5 text-yellow-600 dark:text-yellow-500" /> : 
              <Folder className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
            }
            
            <span className="font-medium text-sm">{folder.name}</span>
            <span className="text-xs text-muted-foreground">
              ({folder.projects.length} {folder.projects.length === 1 ? 'project' : 'projects'})
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setSelectedFolderId(folder.id);
                setShowCreateDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setSelectedFolderId(folder.id);
                setShowCreateFolderDialog(true);
              }}>
                <FolderPlus className="w-4 h-4 mr-2" />
                New Subfolder
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => handleDeleteFolder(folder.id, e)} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {isExpanded && (
          <div className="mt-1">
            {folder.children.map(child => renderFolder(child, depth + 1))}
            {folder.projects.map(project => renderProject(project, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderProject = (project, depth = 0) => {
    const tableCount = project.canvas_state?.tables?.length || 0;
    const relCount = project.canvas_state?.relationships?.length || 0;

    return (
      <Card
        key={project.id}
        draggable
        onDragStart={() => handleDragStart(project)}
        className={`cursor-pointer hover:shadow-md transition-all hover:-translate-y-[2px] ${
          depth > 0 ? 'ml-6' : ''
        }`}
        style={{ marginLeft: depth > 0 ? `${depth * 24}px` : '0' }}
        onClick={() => navigate(`/editor/${project.id}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <Database className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base truncate">{project.name}</h3>
                <div className="flex items-center space-x-3 mt-2 text-xs text-muted-foreground">
                  <span>{tableCount} tables</span>
                  <span>•</span>
                  <span>{relCount} relationships</span>
                </div>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/editor/${project.id}`);
                }}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Open
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => handleDeleteProject(project.id, e)} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  };

  const folderTree = buildFolderTree();
  const rootProjects = projects.filter(p => !p.folder_id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">My Projects</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Welcome back, {user?.name || user?.email}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Action Buttons */}
        <div className="flex items-center space-x-3 mb-6">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
          <Button variant="outline" onClick={() => setShowCreateFolderDialog(true)}>
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
        </div>

        {/* Drop Zone for Root */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 mb-4 transition-colors ${
            dragOverFolder === null && draggedProject ? 'border-primary bg-primary/5' : 'border-transparent'
          }`}
          onDragOver={(e) => handleDragOver(e, null)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, null)}
        >
          <p className="text-sm text-muted-foreground text-center">Drop here to move to root</p>
        </div>

        {/* Folder Tree & Projects */}
        <div className="space-y-2">
          {folderTree.map(folder => renderFolder(folder))}
          
          {rootProjects.length > 0 && (
            <div className="mt-4">
              <h2 className="text-sm font-semibold text-muted-foreground mb-3 px-3">ROOT PROJECTS</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rootProjects.map(project => renderProject(project))}
              </div>
            </div>
          )}
        </div>

        {projects.length === 0 && folders.length === 0 && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">Create your first database schema project</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        )}
      </div>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolderDialog} onOpenChange={setShowCreateFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFolderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
